/**
 * Live-traffic store — the feed seam for the request-flow overlay.
 *
 * A producer (Studio's live-trace SSE hook, or a demo driver) pushes a map of
 * board-edge-id → intensity (particles/sec); the ParticleFlowLayer inside every
 * space reads it and animates dots per edge. Same external-store shape as
 * `connectionHoverStore`: a stable provider value, a snapshot map whose ref
 * changes only when the rates change, so layers re-render only on a real
 * update.
 *
 * Decoupled from board props on purpose — traffic is a property of the running
 * system, injected at the app root, not threaded through the render tree. Absent
 * provider → NOOP → empty map → overlay draws nothing (static/export renders).
 */
import { createContext, useContext, useSyncExternalStore } from "react";

export type EdgeRates = ReadonlyMap<string, number>;

/** Normalize a service / top-level-module name so the board and the trace feed
 *  agree on a key despite casing and separators ("Email Worker" ↔ "email-worker"
 *  ↔ "emailworker"). Mirrors how conform resolves OTel service.name to a module. */
export function normServiceName(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Rate map key for a directed service pair. Both the overlay (from board edge
 *  endpoints) and the live feed (from OTel service names) compute it, so neither
 *  needs the other's id space. */
export function trafficEdgeKey(fromService: string, toService: string): string {
  return normServiceName(fromService) + " " + normServiceName(toService);
}

export interface LiveTrafficStore {
  subscribe(onChange: () => void): () => void;
  getRates(): EdgeRates;
  /** Replace the per-edge rate map (null/empty clears). */
  setRates(rates: EdgeRates | null): void;
}

const EMPTY: EdgeRates = new Map();

export function createLiveTrafficStore(): LiveTrafficStore {
  let rates: EdgeRates = EMPTY;
  const listeners = new Set<() => void>();
  return {
    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    getRates() {
      return rates;
    },
    setRates(next) {
      if (!next || next.size === 0) {
        if (rates.size === 0) return;
        rates = EMPTY;
      } else {
        rates = next;
      }
      for (const l of listeners) l();
    },
  };
}

const Ctx = createContext<LiveTrafficStore | null>(null);
export const LiveTrafficProvider = Ctx.Provider;

const NOOP: LiveTrafficStore = {
  subscribe: () => () => {},
  getRates: () => EMPTY,
  setRates: () => {},
};

/** The ancestor-provided store, or null when no provider is mounted. A host
 *  (Studio) mounts a provider to drive the overlay; BoardCanvas falls back to
 *  an internal store when this is null, so the demo driver still works. */
export function useLiveTrafficStore(): LiveTrafficStore | null {
  return useContext(Ctx);
}

/** Reactive snapshot of the current per-edge rate map (empty without a store). */
export function useLiveEdgeRates(): EdgeRates {
  const store = useContext(Ctx) ?? NOOP;
  return useSyncExternalStore(store.subscribe, store.getRates, store.getRates);
}
